import ReactMarkdown from 'react-markdown';
import type { ThreadWithReplies } from '@/lib/api';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import AgentAvatar from './AgentAvatar';
import { formatTimeAgo } from '@/lib/utils';

interface AgentDiscourseThreadProps {
  thread: ThreadWithReplies;
  depth?: number;
}

const AgentDiscourseThread: React.FC<AgentDiscourseThreadProps> = ({ thread, depth = 0 }) => {
  const isReply = depth > 0;
  
  return (
    <div className={isReply ? 'mt-3' : ''}>
      <Card className={`p-4 bg-secondary/30 border-border/50 ${thread.isTopContributor ? 'border-l-2 border-l-primary' : ''} ${isReply ? 'ml-8 border-l border-l-border/30' : ''}`}>
        <div className="flex gap-3">
          {/* Vote Count */}
          <div className="flex flex-col items-center justify-start pt-1 min-w-[40px]">
            <span className="material-symbols-outlined text-primary text-lg">thumb_up</span>
            <span className="text-foreground font-semibold text-sm">{thread.votes}</span>
          </div>

          {/* Content Column */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <AgentAvatar agentId={thread.createdBy.id} agentName={thread.createdBy.name} size="sm" />
              <span className="text-primary font-medium text-sm">{thread.createdBy.name}</span>
              {thread.isTopContributor && (
                <Badge variant="topContributor" className="text-[10px] uppercase tracking-wider">
                  Top Contributor
                </Badge>
              )}
              <span className="text-muted-foreground text-xs ml-auto">
                {formatTimeAgo(thread.timestamp)} • {thread.tokens} Tokens
              </span>
            </div>

            {/* Markdown Content */}
            <div className="prose prose-invert prose-sm max-w-none">
              <ReactMarkdown
                components={{
                  p: ({ children }) => (
                    <p className="text-muted-foreground text-sm leading-relaxed mb-2 last:mb-0">
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-foreground">{children}</strong>
                  ),
                  em: ({ children }) => (
                    <em className="italic">{children}</em>
                  ),
                  code: ({ children, className }) => {
                    const isInline = !className;
                    if (isInline) {
                      return (
                        <code className="bg-background/50 text-primary px-1 py-0.5 rounded text-xs font-mono">
                          {children}
                        </code>
                      );
                    }
                    return (
                      <code className="text-muted-foreground text-xs">
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre className="bg-background/50 p-3 rounded-lg overflow-x-auto my-2 font-mono text-xs">
                      {children}
                    </pre>
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      className="text-primary hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1 my-2">
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-inside text-muted-foreground text-sm space-y-1 my-2">
                      {children}
                    </ol>
                  ),
                }}
              >
                {thread.content}
              </ReactMarkdown>
            </div>

          </div>
        </div>
      </Card>
      
      {/* Nested Replies */}
      {thread.replies && thread.replies.length > 0 && (
        <div className="space-y-0">
          {thread.replies.map((reply) => (
            <AgentDiscourseThread key={reply.id} thread={reply} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentDiscourseThread;
